import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock } from "lucide-react";
import { loadTemplateFonts } from "@/lib/templateFonts";

loadTemplateFonts();

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

const InnerForm = ({ paymentIntentId, isSubscription, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const returnUrl = `${window.location.origin}/payment-success?provider=stripe${isSubscription ? "&flow=subscription" : ""}`;
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try another card.");
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      onSuccess(paymentIntentId);
      return;
    }
    // Some payment methods redirect away; if we're still here, treat as pending.
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 bg-transparent border border-slate-200 text-neutral-600 hover:bg-slate-100 py-2.5 text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          data-testid="stripe-pay-submit"
          className="flex-1 bg-[#635BFF] text-white hover:bg-[#5249e6] py-2.5 text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-3.5 h-3.5" /> Pay Now</>}
        </button>
      </div>
    </form>
  );
};

export const StripePaymentForm = ({ clientSecret, paymentIntentId, isSubscription, onSuccess, onCancel }) => {
  if (!clientSecret) return null;
  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#A67C52",
        colorBackground: "#ffffff",
        colorText: "#16332A",
        colorDanger: "#EF4444",
        fontFamily: "IBM Plex Sans, sans-serif",
        borderRadius: "2px",
      },
    },
  };
  return (
    <Elements stripe={stripePromise} options={options}>
      <InnerForm paymentIntentId={paymentIntentId} isSubscription={isSubscription} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};

export default StripePaymentForm;
